// SPDX-License-Identifier: GPL-3.0-or-later
//
// Decode a job payload arriving on the URL hash (#/handoff?p=<base64url>) and
// hand it to the store. Phase 3 uses URL fragments for simplicity; a future
// phase can upgrade to chrome.storage.session + content-script injection for
// larger payloads and to keep job text out of browser history.

import { addApplication, type AddResult, type JobPayload } from './store/applications';

function base64UrlDecode(s: string): string {
  const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  // atob -> binary string; decode UTF-8 back to a JS string.
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

function isJobPayload(x: unknown): x is JobPayload {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.jobTitle === 'string' &&
    typeof o.companyName === 'string' &&
    typeof o.jobUrl === 'string' &&
    typeof o.applyType === 'string' &&
    typeof o.description === 'string'
  );
}

export interface HandoffOutcome {
  ok: true;
  result: AddResult;
}
export interface HandoffFailure {
  ok: false;
  message: string;
}

export async function ingestHandoff(
  params: Record<string, string>,
): Promise<HandoffOutcome | HandoffFailure> {
  const p = params['p'];
  if (!p) return { ok: false, message: 'No payload in handoff URL.' };
  let payload: unknown;
  try {
    payload = JSON.parse(base64UrlDecode(p));
  } catch (err) {
    return { ok: false, message: `Could not decode payload: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!isJobPayload(payload)) return { ok: false, message: 'Payload missing required fields.' };
  if (!payload.jobTitle.trim()) return { ok: false, message: 'Job title is empty.' };

  try {
    const result = await addApplication({
      ...payload,
      companyName: payload.companyName.trim() || 'Unknown Company',
    });
    return { ok: true, result };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}
